import React, { useCallback, useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ParticipantAdaptiveGrid } from "./ParticipantAdaptiveGrid";
import { ParticipantLayoutPagination } from "./ParticipantLayoutPagination";
import { ParticipantTile, getContainedScreenShareFrameSize } from "./ParticipantTile";
import { createParticipantAdaptiveGrid } from "./participant-adaptive-grid";
import { clampParticipantLayoutPageIndex } from "./participant-layout-pagination";
import { SCREEN_SHARE_TILE_KIND } from "./participant-layout-participants";

export const PARTICIPANT_SPOTLIGHT_LAYER_INDEX = {
  stage: 0,
  pip: 1
};

const PARTICIPANT_SPOTLIGHT_GAP_PX = 16;
const PARTICIPANT_SPOTLIGHT_COMPACT_GAP_PX = 12;
const PARTICIPANT_SPOTLIGHT_COMPACT_GAP_MAX_HEIGHT_PX = 560;
const PARTICIPANT_SPOTLIGHT_SCREEN_SHARE_ASPECT_RATIO = 16 / 9;
const PARTICIPANT_SPOTLIGHT_MAX_VISIBLE_SECONDARY_ITEMS = 8;
const PARTICIPANT_SPOTLIGHT_MOBILE_MAX_WIDTH_PX = 640;
const PARTICIPANT_SPOTLIGHT_MOBILE_PAGINATION_INSET_PX = 52;
const PARTICIPANT_SPOTLIGHT_MIN_MOBILE_SECONDARY_HEIGHT_PX = 96;
const PARTICIPANT_SPOTLIGHT_PIP_FLOAT_BREAKPOINTS = [
  { minWidth: 0, width: 250, height: 141 },
  { minWidth: 360, width: 285, height: 160 },
  { minWidth: 640, width: 300, height: 169 },
  { minWidth: 768, width: 320, height: 180 },
  { minWidth: 1024, width: 340, height: 191 },
  { minWidth: 1440, width: 360, height: 203 }
];

const useStyles = makeStyles({
  root: {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    pointerEvents: "auto"
  },
  emptyFrame: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--colorNeutralBackground4)"
  },
  tileFrame: {
    position: "relative",
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0
  },
  stageClipFrame: {
    position: "relative",
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden"
  },
  containedScreenShareTileFrame: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "var(--participant-spotlight-contained-width)",
    height: "var(--participant-spotlight-contained-height)",
    transform: "translate(-50%, -50%)"
  },
  paginationLayer: {
    position: "absolute",
    right: 0,
    bottom: tokens.spacingVerticalS,
    left: 0,
    zIndex: 2,
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none"
  },
  paginationControls: {
    pointerEvents: "auto"
  }
});

function usesScreenSharePipLayout(layout, participants) {
  return (
    layout.variant === "stage-with-strip" &&
    participants.length === 2 &&
    participants[0]?.kind === SCREEN_SHARE_TILE_KIND &&
    !participants[0]?.isPinned &&
    !participants[1]?.isPinned
  );
}

function usesTwoItemPinnedSplitLayout(layout, participants) {
  return (
    layout.variant === "stage-with-strip" &&
    participants.length === 2 &&
    !usesScreenSharePipLayout(layout, participants)
  );
}

function usesCurrentPageTwoItemPinnedSplitLayout({ layout, participants, currentVisiblePage, maxVisible }) {
  if (usesTwoItemPinnedSplitLayout(layout, participants)) {
    return true;
  }

  if (
    layout.variant !== "stage-with-strip" ||
    !maxVisible ||
    participants.length <= 2 ||
    usesScreenSharePipLayout(layout, participants)
  ) {
    return false;
  }

  const secondaryParticipantCount = Math.max(0, participants.length - 1);
  const secondaryPageStartIndex = currentVisiblePage * maxVisible;
  const secondaryItemsOnPage = Math.max(0, Math.min(maxVisible, secondaryParticipantCount - secondaryPageStartIndex));

  return secondaryItemsOnPage === 1;
}

function shouldHideParticipantPinButton(participant, gridItem, participants) {
  return (
    participants.length === 1 ||
    (participant.kind === SCREEN_SHARE_TILE_KIND && gridItem?.isMain && participants.length === 2)
  );
}

function shouldHideParticipantFullscreenButton(participants) {
  return participants.length === 1;
}

function usesContainedScreenShareStageFrame(participant, gridItem) {
  return Boolean(participant.kind === SCREEN_SHARE_TILE_KIND && participant.isPinned && gridItem?.isMain);
}

function getContainedScreenShareStageFrameStyle(gridItem) {
  const frameSize = getContainedScreenShareFrameSize(
    gridItem?.dimensions?.width,
    gridItem?.dimensions?.height,
    PARTICIPANT_SPOTLIGHT_SCREEN_SHARE_ASPECT_RATIO
  );

  if (!frameSize) {
    return undefined;
  }

  return {
    "--participant-spotlight-contained-width": `${frameSize.width}px`,
    "--participant-spotlight-contained-height": `${frameSize.height}px`
  };
}

function usesPinnedZoomLayout({ layout, participants, zoomPinnedParticipant }) {
  return Boolean(
    zoomPinnedParticipant &&
      layout.variant === "stage-with-strip" &&
      participants.length > 1 &&
      !usesScreenSharePipLayout(layout, participants)
  );
}

function getParticipantSpotlightPinnedIndex(layout, participants) {
  if (usesScreenSharePipLayout(layout, participants)) {
    return undefined;
  }

  return layout.variant === "stage-with-strip" ? 0 : undefined;
}

function toParticipantSpotlightDimensions(dimensions) {
  if (Number.isFinite(dimensions?.width) && Number.isFinite(dimensions?.height)) {
    return {
      width: Math.max(0, dimensions.width),
      height: Math.max(0, dimensions.height)
    };
  }

  if (typeof window === "undefined") {
    return null;
  }

  return {
    width: window.innerWidth || 0,
    height: window.innerHeight || 0
  };
}

function getInsetParticipantSpotlightDimensions(dimensions, bottomInset = 0) {
  const layoutDimensions = toParticipantSpotlightDimensions(dimensions);
  const safeBottomInset = Number.isFinite(bottomInset) ? Math.max(0, bottomInset) : 0;

  if (!layoutDimensions || safeBottomInset <= 0) {
    return layoutDimensions;
  }

  return {
    ...layoutDimensions,
    height: Math.max(0, layoutDimensions.height - safeBottomInset)
  };
}

function getMobileParticipantSpotlightMaxVisible(layout, participants, dimensions, bottomInset = 0) {
  const layoutDimensions = getInsetParticipantSpotlightDimensions(dimensions, bottomInset);

  if (
    !layoutDimensions ||
    layout.variant !== "stage-with-strip" ||
    layoutDimensions.width > PARTICIPANT_SPOTLIGHT_MOBILE_MAX_WIDTH_PX
  ) {
    return PARTICIPANT_SPOTLIGHT_MAX_VISIBLE_SECONDARY_ITEMS;
  }

  const secondaryParticipantCount = Math.max(0, participants.length - 1);
  const maxVisibleCandidate = Math.min(PARTICIPANT_SPOTLIGHT_MAX_VISIBLE_SECONDARY_ITEMS, secondaryParticipantCount);

  for (let maxVisible = maxVisibleCandidate; maxVisible > 0; maxVisible--) {
    const grid = createParticipantAdaptiveGrid({
      count: participants.length,
      dimensions: layoutDimensions,
      gap: PARTICIPANT_SPOTLIGHT_GAP_PX,
      layoutMode: "gallery",
      maxVisible,
      othersPosition: "right",
      pinnedIndex: 0
    });
    const secondaryItems = grid.items.filter(gridItem => !gridItem.isMain && !gridItem.isFloat);

    if (
      secondaryItems.length > 0 &&
      secondaryItems.every(
        gridItem => gridItem.dimensions.height >= PARTICIPANT_SPOTLIGHT_MIN_MOBILE_SECONDARY_HEIGHT_PX
      )
    ) {
      return maxVisible;
    }
  }

  return Math.min(1, maxVisibleCandidate);
}

function getParticipantSpotlightMaxVisible(layout, participants, dimensions, bottomInset = 0) {
  if (usesScreenSharePipLayout(layout, participants) || usesTwoItemPinnedSplitLayout(layout, participants)) {
    return undefined;
  }

  return layout.variant === "stage-with-strip"
    ? getMobileParticipantSpotlightMaxVisible(layout, participants, dimensions, bottomInset)
    : undefined;
}

function getParticipantSpotlightMobilePaginationInset({ dimensions, layout, maxVisible, participants }) {
  const layoutDimensions = toParticipantSpotlightDimensions(dimensions);
  const secondaryParticipantCount = Math.max(0, participants.length - 1);
  const secondaryPageCount = maxVisible ? Math.ceil(secondaryParticipantCount / maxVisible) : 1;

  return layout.variant === "stage-with-strip" &&
    layoutDimensions &&
    layoutDimensions.width <= PARTICIPANT_SPOTLIGHT_MOBILE_MAX_WIDTH_PX &&
    secondaryPageCount > 1
    ? PARTICIPANT_SPOTLIGHT_MOBILE_PAGINATION_INSET_PX
    : 0;
}

function getParticipantSpotlightGap(layout, dimensions) {
  const layoutDimensions = toParticipantSpotlightDimensions(dimensions);

  return layout.variant === "stage-with-strip" &&
    layoutDimensions &&
    (layoutDimensions.width <= PARTICIPANT_SPOTLIGHT_MOBILE_MAX_WIDTH_PX ||
      layoutDimensions.height <= PARTICIPANT_SPOTLIGHT_COMPACT_GAP_MAX_HEIGHT_PX)
    ? PARTICIPANT_SPOTLIGHT_COMPACT_GAP_PX
    : PARTICIPANT_SPOTLIGHT_GAP_PX;
}

function getPinnedZoomParticipants(participants) {
  if (participants.length !== 2) {
    return participants;
  }

  const pinnedParticipant = participants.find(
    participant => participant.isPinned && participant.kind !== SCREEN_SHARE_TILE_KIND
  );
  const screenShareParticipant = participants.find(participant => participant.kind === SCREEN_SHARE_TILE_KIND);

  if (!pinnedParticipant || !screenShareParticipant) {
    return participants;
  }

  return [pinnedParticipant, screenShareParticipant];
}

function getParticipantSpotlightSlot(layout, gridItem) {
  const baseSlot = layout.slots[gridItem.index] || {
    index: gridItem.index,
    placement: "grid"
  };

  if (gridItem.isMain) {
    return {
      ...baseSlot,
      emphasis: "primary",
      placement: "center",
      role: "stage"
    };
  }

  if (gridItem.isFloat) {
    return {
      ...baseSlot,
      emphasis: "secondary",
      placement: "bottom-right",
      role: "pip"
    };
  }

  return {
    ...baseSlot,
    emphasis: "secondary",
    placement: "grid",
    role: "tile"
  };
}

export function ParticipantSpotlightLayout({
  dimensions,
  hasStatusTray = false,
  layout,
  participants,
  onRequestParticipantFullscreen,
  onRequestParticipantMute,
  onToggleParticipantMuted,
  onToggleParticipantPinned,
  onToggleParticipantRoomSpotlight,
  zoomPinnedParticipant = false
}) {
  const styles = useStyles();
  const [secondaryPageIndex, setSecondaryPageIndex] = useState(0);
  const pinnedIndex = getParticipantSpotlightPinnedIndex(layout, participants);
  const spotlightGap = getParticipantSpotlightGap(layout, dimensions);
  const baseMaxVisible = getParticipantSpotlightMaxVisible(layout, participants, dimensions);
  const paginationBottomInset = getParticipantSpotlightMobilePaginationInset({
    dimensions,
    layout,
    maxVisible: baseMaxVisible,
    participants
  });
  const maxVisible =
    paginationBottomInset > 0
      ? getParticipantSpotlightMaxVisible(layout, participants, dimensions, paginationBottomInset)
      : baseMaxVisible;
  const useTwoItemPinnedSplit = usesCurrentPageTwoItemPinnedSplitLayout({
    currentVisiblePage: secondaryPageIndex,
    layout,
    maxVisible,
    participants
  });
  const usePinnedZoom = usesPinnedZoomLayout({ layout, participants, zoomPinnedParticipant });
  const zoomParticipants = usePinnedZoom ? getPinnedZoomParticipants(participants) : participants;
  const zoomVisibleParticipants = usePinnedZoom ? zoomParticipants.slice(0, 1) : participants;
  const secondaryPageCount = maxVisible ? Math.ceil(Math.max(0, participants.length - 1) / maxVisible) : 1;
  const effectivePaginationBottomInset = secondaryPageCount > 1 ? paginationBottomInset : 0;
  const setClampedSecondaryPageIndex = useCallback((nextPageIndex, pageCount) => {
    setSecondaryPageIndex(clampParticipantLayoutPageIndex(nextPageIndex, pageCount));
  }, []);
  const renderParticipantTileFrame = useCallback(
    ({
      gridItem,
      hidePinButton = false,
      hideFullscreenButton = false,
      index,
      isFullscreenActive = false,
      participant,
      slot,
      extraAttributes = {}
    }) => {
      const useContainedScreenShareStageFrame = usesContainedScreenShareStageFrame(participant, gridItem);
      const participantTileFrame = (
        <div
          className={mergeClasses(
            styles.tileFrame,
            useContainedScreenShareStageFrame && styles.containedScreenShareTileFrame
          )}
          data-participant-spotlight-contained-screen-share={useContainedScreenShareStageFrame ? "" : undefined}
          data-participant-id={participant.id}
          data-participant-kind={participant.kind}
          data-participant-spotlight-float={gridItem?.isFloat ? "" : undefined}
          data-participant-spotlight-index={index}
          data-participant-spotlight-item=""
          data-participant-spotlight-main={gridItem?.isMain ? "true" : "false"}
          data-participant-spotlight-secondary-item={!gridItem?.isMain && !gridItem?.isFloat ? "" : undefined}
          data-participant-spotlight-stage={gridItem?.isMain ? "" : undefined}
          data-slot-role={slot.role}
          style={useContainedScreenShareStageFrame ? getContainedScreenShareStageFrameStyle(gridItem) : undefined}
          {...extraAttributes}
        >
          <ParticipantTile
            hideFullscreenButton={hideFullscreenButton}
            hidePinButton={hidePinButton}
            isFullscreenActive={isFullscreenActive}
            participant={participant}
            slot={slot}
            onRequestParticipantFullscreen={onRequestParticipantFullscreen}
            onRequestParticipantMute={onRequestParticipantMute}
            onToggleParticipantMuted={onToggleParticipantMuted}
            onToggleParticipantPinned={onToggleParticipantPinned}
            onToggleParticipantRoomSpotlight={onToggleParticipantRoomSpotlight}
          />
        </div>
      );

      return gridItem?.isMain ? (
        <div className={styles.stageClipFrame} data-participant-spotlight-stage-clip="">
          {participantTileFrame}
        </div>
      ) : (
        participantTileFrame
      );
    },
    [
      onRequestParticipantFullscreen,
      onRequestParticipantMute,
      onToggleParticipantMuted,
      onToggleParticipantPinned,
      onToggleParticipantRoomSpotlight,
      styles.containedScreenShareTileFrame,
      styles.stageClipFrame,
      styles.tileFrame
    ]
  );

  useLayoutEffect(() => {
    setSecondaryPageIndex(0);
  }, [participants.length, pinnedIndex]);

  if (layout.variant === "empty" || participants.length === 0) {
    return (
      <div className={styles.root} data-participant-spotlight-layout="adaptive-empty">
        <div className={styles.emptyFrame} data-participant-spotlight-empty="" />
      </div>
    );
  }

  if (usePinnedZoom) {
    return (
      <div
        className={styles.root}
        data-participant-spotlight-has-status-tray={hasStatusTray ? "true" : "false"}
        data-participant-spotlight-layout="adaptive-zoom"
      >
        <ParticipantAdaptiveGrid
          dimensions={dimensions}
          gap={0}
          itemAspectRatios={zoomVisibleParticipants.map(() => "auto")}
          items={zoomVisibleParticipants}
          layoutMode="spotlight"
          pinnedIndex={0}
          renderItem={({ gridItem, index, item: participant }) =>
            renderParticipantTileFrame({
              gridItem,
              index,
              isFullscreenActive: gridItem.isMain,
              participant,
              slot: getParticipantSpotlightSlot(layout, gridItem)
            })
          }
        />
      </div>
    );
  }

  return (
    <div
      className={styles.root}
      data-participant-spotlight-has-status-tray={hasStatusTray ? "true" : "false"}
      data-participant-spotlight-layout="adaptive"
    >
      <ParticipantAdaptiveGrid
        bottomInset={effectivePaginationBottomInset}
        currentVisiblePage={secondaryPageIndex}
        dimensions={dimensions}
        floatBreakpoints={PARTICIPANT_SPOTLIGHT_PIP_FLOAT_BREAKPOINTS}
        gap={spotlightGap}
        items={participants}
        layoutMode={layout.variant === "single" ? "spotlight" : "gallery"}
        maxVisible={maxVisible}
        othersPosition="right"
        pinnedIndex={pinnedIndex}
        renderItem={({ gridItem, index, item: participant }) => {
          const slot = getParticipantSpotlightSlot(layout, gridItem);
          const hidePinButton = shouldHideParticipantPinButton(participant, gridItem, participants);
          const hideFullscreenButton = shouldHideParticipantFullscreenButton(participants);

          return renderParticipantTileFrame({
            gridItem,
            hideFullscreenButton,
            hidePinButton,
            index,
            participant,
            slot
          });
        }}
        renderOverlay={({ grid }) =>
          grid.pagination.enabled ? (
            <div className={styles.paginationLayer}>
              <div className={styles.paginationControls}>
                <ParticipantLayoutPagination
                  onPageChange={nextPageIndex =>
                    setClampedSecondaryPageIndex(nextPageIndex, grid.pagination.totalPages)
                  }
                  pageCount={grid.pagination.totalPages}
                  pageIndex={grid.pagination.currentPage}
                />
              </div>
            </div>
          ) : null
        }
        onSwipeNextPage={
          secondaryPageIndex < secondaryPageCount - 1
            ? () => setClampedSecondaryPageIndex(secondaryPageIndex + 1, secondaryPageCount)
            : undefined
        }
        onSwipePreviousPage={
          secondaryPageIndex > 0
            ? () => setClampedSecondaryPageIndex(secondaryPageIndex - 1, secondaryPageCount)
            : undefined
        }
        swipeEnabled
        twoItemPinnedSplit={useTwoItemPinnedSplit}
      />
    </div>
  );
}

ParticipantSpotlightLayout.propTypes = {
  dimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  hasStatusTray: PropTypes.bool,
  layout: PropTypes.shape({
    slots: PropTypes.arrayOf(
      PropTypes.shape({
        index: PropTypes.number.isRequired,
        role: PropTypes.string.isRequired
      })
    ).isRequired,
    variant: PropTypes.string.isRequired
  }).isRequired,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  ).isRequired,
  onToggleParticipantMuted: PropTypes.func.isRequired,
  onRequestParticipantFullscreen: PropTypes.func,
  onRequestParticipantMute: PropTypes.func,
  onToggleParticipantPinned: PropTypes.func.isRequired,
  onToggleParticipantRoomSpotlight: PropTypes.func,
  zoomPinnedParticipant: PropTypes.bool
};
